import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver, ResolverResult } from 'react-hook-form';
import { countryToIso2OrNull } from '@/features/country/country-code';

// Phones are stored in international (+CC) form, so a validity check can parse
// the embedded country — no per-field country context needed. Uses
// `isValidPhoneNumber` (matches against the country's real dial-plan patterns),
// not `isPossiblePhoneNumber` (a loose length-bucket check that accepts e.g. an
// over-length KR mobile number — BUG-01: "010-XXXX-XXXXX" was silently accepted
// despite exceeding the valid length for that pattern).
//
// MUST import from '/max' (full metadata), not '/min' or the bare package
// (both are the same reduced metadata and don't carry per-type patterns —
// `.getType()` returns undefined on either). Confirmed empirically: with '/min',
// "+82 1011 1111" (8 digits, LOOKS like a truncated mobile number) validates as
// true — the reduced metadata only tracks aggregate possible-lengths per
// country, not which digit patterns actually belong to which number type, so an
// 8-digit string coincidentally clears SOME valid-length bucket for KR. '/max'
// correctly rejects it while still accepting real KR mobile (10 digits) and
// real KR fixed-line (8-9 digits, genuinely different area-code prefixes).
// '/mobile' was considered (smaller than '/max') but rejects real KR
// fixed-line numbers outright — these fields are general business phones, not
// mobile-only, so '/mobile' would false-reject legitimate landlines.
//
// '' is allowed here; required-ness is enforced by `.min(1)`.
const isValidPhone = (v: string) => v === '' || isValidPhoneNumber(v);
const PHONE_MSG = 'Enter a valid phone number for the selected country';

/** A required free-text field: non-empty, at most `max` characters. */
const requiredText = (message: string, max = 255) =>
  z.string().min(1, message).max(max, `Max ${max} characters`);

// Length caps sized to the longest real-world value, with headroom.
// Fields with no cap of their own below (website, signature date) keep 255.
const COMPANY_NAME_MAX = 200; // UK Companies House caps company names at 160
const PERSON_NAME_MAX = 100; // UK Government Data Standards: 70 for a full name
const JOB_TITLE_MAX = 100; // job title and department — no standard; real ones rarely pass 80
const CITY_MAX = 200; // Bangkok's full ceremonial name: 168 letters, 182 with spaces
const ADDRESS_MAX = 200; // one field holds street + suite
const PHONE_MAX = 25; // E.164: 15 digits, plus "+" and spacing
export const EMAIL_MAX = 254; // RFC 5321 path limit
const EMAIL_DOMAIN_MAX = 254; // 253-character domain + optional leading "@"
const COUNTRY_MAX = 60; // longest in iso-countries.ts is 44
const QMS_STANDARD_MAX = 150;
/** Postal codes top out around 10 digits (Iran); 20 leaves room for spacing. */
const POSTAL_MAX = 20;
const requiredPostal = requiredText('Postal code is required', POSTAL_MAX);
const optionalPostal = z.string().max(POSTAL_MAX, `Max ${POSTAL_MAX} characters`).optional();

/** Optional free text with no specific cap of its own: short inputs get 200,
 *  multi-line textareas 1000. */
const SHORT_TEXT_MAX = 200;
export const LONG_TEXT_MAX = 1000;
export const optionalShortText = z.string().max(SHORT_TEXT_MAX, `Max ${SHORT_TEXT_MAX} characters`).optional();
const optionalLongText = z.string().max(LONG_TEXT_MAX, `Max ${LONG_TEXT_MAX} characters`).optional();

// Domain-shape check shared by `email_domain` and `hq_website` (its hostname).
// Issue #215: the old `email_domain` regex's character class included `.`
// itself, so it only required the string to END in `.xx+` — leading/trailing/
// consecutive dots (e.g. "brightwelllogistics-..com") still passed. And
// `hq_website` used a bare `.url()`, which only checks that `new URL(...)`
// parses — no TLD plausibility check, so multi-single-letter "domains" like
// "brightwell.c.o.m" (TLD "m") passed too. This requires a final label of
// 2+ letters and disallows empty labels, matching the rigor `.email()`
// already applies to full-email fields elsewhere in this file.
const DOMAIN_REGEX = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i;
const isValidDomain = (domain: string) => DOMAIN_REGEX.test(domain);

/**
 * Customer Account Zod schemas — per-section + composed full-form.
 *
 * Field names match the vanilla form's flat key-value scheme exactly so the
 * payload shape stays byte-identical to what the FastAPI backend expects.
 * Do NOT rename fields without coordinating with backend's DraftPayload /
 * CustomerSubmissionResponse schemas in `backend/schemas.py`.
 *
 * Regulatory note: the customer form was reviewed by a regulatory expert.
 * Field labels and required/optional status are intentional — preserve them
 * verbatim from the vanilla form.
 */

// ---------------------------------------------------------------------------
// Section 1 — Legal Company Identification (14 fields, 7 required)
// ---------------------------------------------------------------------------

const COMPANY_TYPES = [
  'corporation',
  'llc',
  'partnership',
  'sole_proprietorship',
  'nonprofit',
  'government',
  'gmbh',
  'ag',
  'sarl',
  'sa',
  'ltd',
  'plc',
  'bv',
  'other',
] as const;

// DUNS = 9 digits; separators (dashes/spaces) are cosmetic and OPTIONAL — a
// validly-grouped number must not be rejected for where its dashes fall. The
// onBlur formatter (formatDuns) still normalizes the display to XX-XXX-XXXX.
const dunsSchema = z
  .string()
  .refine(
    (v) => v === '' || /^\d{9}$/.test(v.replace(/[-\s]/g, '')),
    'Enter a 9-digit DUNS number (dashes optional)',
  )
  .optional();

// BASE (FROM) — who the company IS, true regardless of where it sells. The US
// FDA identifiers (EIN, FEI, CAGE, establishment/owner-operator numbers) are NOT
// here — they're a US-market registration (TO), in `usMarketSchema` below.
export const companyIdentificationSchema = z.object({
  // Required
  legal_company_name: requiredText('Legal company name is required', COMPANY_NAME_MAX),

  company_type: z.enum(COMPANY_TYPES, {
    message: 'Select a company type',
  }),

  country_incorporation: requiredText('Country of incorporation is required', COUNTRY_MAX),

  year_founded: z
    .number({ message: 'Year founded is required' })
    .int('Year must be a whole number')
    .min(1800, 'Year must be 1800 or later')
    .max(new Date().getFullYear(), `Year must be ${new Date().getFullYear()} or earlier`),

  // Optional
  // Not every jurisdiction has a "state of incorporation" (e.g. a German GmbH),
  // so this is optional.
  state_incorporation: optionalShortText,
  dba_name: optionalShortText,
  // Parent company in the account this entity rolls up under (corp ↔ LLC).
  // Becomes a self-FK (parent_company_id) once companies are persisted.
  parent_company: z.string().optional(),
  eori_number: optionalShortText,
  duns_number: dunsSchema,
  // Free-text company type when company_type is 'other'. Unconditionally
  // optional at THIS layer — the "required when 'other' is picked" rule is a
  // cross-field check (needs company_type), attached at the composed
  // customerAccountSchema level below (matches every other conditional
  // requirement in this file — US Agent, CA/AU rep, State/Province).
  company_type_other: optionalShortText,
});

export type CompanyIdentificationValues = z.infer<typeof companyIdentificationSchema>;

// ---------------------------------------------------------------------------
// Section 1's COMPANY_TYPE_OPTIONS — exposed for the <Select> dropdown.
// Label text matches the vanilla form's <option> labels exactly.
// ---------------------------------------------------------------------------

export const COMPANY_TYPE_OPTIONS: { value: typeof COMPANY_TYPES[number]; label: string }[] = [
  { value: 'corporation', label: 'Corporation' },
  { value: 'llc', label: 'Limited Liability Company (LLC)' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'nonprofit', label: 'Non-Profit Organization' },
  { value: 'government', label: 'Government Entity' },
  { value: 'gmbh', label: 'GmbH (Germany)' },
  { value: 'ag', label: 'AG (Germany)' },
  { value: 'sarl', label: 'SARL (France)' },
  { value: 'sa', label: 'SA (France)' },
  { value: 'ltd', label: 'Ltd. (UK)' },
  { value: 'plc', label: 'plc (UK)' },
  { value: 'bv', label: 'BV (Netherlands)' },
  { value: 'other', label: 'Other' },
];

/**
 * Chip-sized company types. The full labels above are written for a <Select>,
 * where the country suffix disambiguates ("GmbH (Germany)"); in a table cell
 * they are the widest thing in the row for no benefit — the roster is already
 * scoped to one company's group. Callers render this and put the full label in
 * a ``title``, so nothing is lost.
 *
 * Same keys as COMPANY_TYPE_OPTIONS; a value missing here falls back to its
 * full label.
 */
export const COMPANY_TYPE_SHORT_LABELS: Record<string, string> = {
  corporation: 'Corporation',
  llc: 'LLC',
  partnership: 'Partnership',
  sole_proprietorship: 'Sole Proprietorship',
  nonprofit: 'Non-Profit',
  government: 'Government',
  gmbh: 'GmbH',
  ag: 'AG',
  sarl: 'SARL',
  sa: 'SA',
  ltd: 'Ltd.',
  plc: 'plc',
  bv: 'BV',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Default values for a fresh draft (Section 1).
// Each field gets a deterministic default so RHF + Zod can wire correctly.
// ---------------------------------------------------------------------------

export const companyIdentificationDefaults: Partial<CompanyIdentificationValues> = {
  legal_company_name: '',
  dba_name: '',
  parent_company: '',
  eori_number: '',
  country_incorporation: '',
  state_incorporation: '',
  duns_number: '',
  company_type_other: '',
  // `company_type` and `year_founded` intentionally omitted — RHF leaves them
  // undefined so the placeholder shows and Zod validation kicks in on submit.
};

// ---------------------------------------------------------------------------
// Section 2 — Corporate Headquarters Information (8 fields, 7 required;
// State/Province is optional — not every jurisdiction has one — BUG-03)
// ---------------------------------------------------------------------------

export const corporateHeadquartersSchema = z.object({
  hq_address: requiredText('Headquarters address is required', ADDRESS_MAX),
  hq_city: requiredText('City is required', CITY_MAX),
  hq_state: optionalShortText,
  hq_postal: requiredPostal,
  hq_country: requiredText('Country is required', COUNTRY_MAX),
  hq_phone: requiredText('Corporate phone number is required', PHONE_MAX)
    .refine(isValidPhone, PHONE_MSG),
  hq_website: requiredText('Corporate website is required')
    .url('Must be a valid URL (e.g., https://www.example.com)')
    .refine((v) => {
      try {
        const url = new URL(v);
        // The scheme must be checked here: zod's `.url()` takes no `protocol`
        // option in this schema, so it only asserts `new URL(v)` parses — and
        // `formatWebsite` deliberately leaves any existing scheme alone rather
        // than prepending `https://`. Without this, `ftp://example.com` has a
        // perfectly valid hostname and passes the client, then fails
        // `_is_valid_website` in backend/services/company_validation.py with a
        // 422 the form never surfaces. Keep the two in sync.
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
        return isValidDomain(url.hostname);
      } catch {
        return false;
      }
    }, 'Must be a valid website URL (e.g., https://www.example.com)'),
  email_domain: requiredText('Corporate email domain is required', EMAIL_DOMAIN_MAX)
    .refine((v) => isValidDomain(v.replace(/^@+/, '')), 'Format: example.com or @example.com'),
});

export type CorporateHeadquartersValues = z.infer<typeof corporateHeadquartersSchema>;

export const corporateHeadquartersDefaults: Partial<CorporateHeadquartersValues> = {
  hq_address: '',
  hq_city: '',
  hq_state: '',
  hq_postal: '',
  hq_country: '',
  hq_phone: '',
  hq_website: '',
  email_domain: '',
};

// ---------------------------------------------------------------------------
// Section 3 — FDA Regulatory Contact Information
//
// 3 sub-sections, 15 fields total:
//   Primary contact (6 required) — name, title, department, phone, email, address
//   Secondary contact (4 optional) — name, title, phone, email
//   U.S. Agent (5 optional, but FDA-required for foreign-incorporated companies)
// ---------------------------------------------------------------------------

// Optional email: accept empty string OR a valid email.
const optionalEmail = z
  .string()
  .max(EMAIL_MAX, `Max ${EMAIL_MAX} characters`)
  .email('Invalid email')
  .or(z.literal(''))
  .optional();

// Optional phone: accept empty string OR a possible phone number.
const optionalPhone = z
  .string()
  .refine(isValidPhone, PHONE_MSG)
  .optional();

export const fdaContactSchema = z.object({
  // Primary (all required)
  primary_contact_name: requiredText('Name is required', PERSON_NAME_MAX),
  primary_contact_title: requiredText('Title is required', JOB_TITLE_MAX),
  primary_contact_department: requiredText('Department is required', JOB_TITLE_MAX),
  primary_contact_phone: requiredText('Phone number is required', PHONE_MAX)
    .refine(isValidPhone, PHONE_MSG),
  primary_contact_email: requiredText('Email is required', EMAIL_MAX)
    .email('Enter a valid email address'),
  primary_contact_address: requiredText('Mailing address is required', ADDRESS_MAX),
  primary_contact_city: requiredText('City is required', CITY_MAX),
  primary_contact_state: optionalShortText,
  primary_contact_postal: requiredPostal,
  primary_contact_country: requiredText('Country is required', COUNTRY_MAX),

  // Secondary (optional)
  secondary_contact_name: optionalShortText,
  secondary_contact_title: optionalShortText,
  secondary_contact_phone: optionalPhone,
  secondary_contact_email: optionalEmail,

  // (U.S. Agent moved to usMarketSchema — it's a US-market role, TO.)

  // Billing contact (base — applies across every market)
  billing_contact_name: optionalShortText,
  billing_contact_title: optionalShortText,
  billing_contact_email: optionalEmail,
  billing_invoice_address: optionalShortText,
  billing_invoice_city: optionalShortText,
  billing_invoice_state: optionalShortText,
  billing_invoice_postal: optionalPostal,
  billing_invoice_country: optionalShortText,
});

export type FdaContactValues = z.infer<typeof fdaContactSchema>;

export const fdaContactDefaults: Partial<FdaContactValues> = {
  primary_contact_name: '',
  primary_contact_title: '',
  primary_contact_department: '',
  primary_contact_phone: '',
  primary_contact_email: '',
  primary_contact_address: '',
  primary_contact_city: '',
  primary_contact_state: '',
  primary_contact_postal: '',
  primary_contact_country: '',
  secondary_contact_name: '',
  secondary_contact_title: '',
  secondary_contact_phone: '',
  secondary_contact_email: '',
  billing_contact_name: '',
  billing_contact_title: '',
  billing_contact_email: '',
  billing_invoice_address: '',
  billing_invoice_city: '',
  billing_invoice_state: '',
  billing_invoice_postal: '',
  billing_invoice_country: '',
};

// ---------------------------------------------------------------------------
// Section 5 — Establishment Registration Information
//
// First dynamic-list section: 1..N facilities, each with 8 fields.
// Uses RHF `useFieldArray` on the `facilities` array.
// ---------------------------------------------------------------------------

const FACILITY_TYPES = [
  'manufacturing',
  'testing',
  'packaging',
  'sterilization',
  'clinical',
] as const;

export const FACILITY_TYPE_OPTIONS: { value: typeof FACILITY_TYPES[number]; label: string }[] = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'testing', label: 'Testing Laboratory' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'sterilization', label: 'Sterilization' },
  { value: 'clinical', label: 'Clinical' },
];

// Relationship of each site/manufacturer to the company — the consolidation tag
// that folds the former Manufacturing Legal Entities, Facilities & Sites, and
// Contract-manufacturer sections into ONE "Facilities & Sites Information" list.
const FACILITY_RELATIONSHIPS = ['own_site', 'affiliated', 'contracted'] as const;
export const FACILITY_RELATIONSHIP_OPTIONS: {
  value: typeof FACILITY_RELATIONSHIPS[number];
  label: string;
}[] = [
  { value: 'own_site', label: 'Own site' },
  { value: 'affiliated', label: 'Affiliated' },
  { value: 'contracted', label: 'Contracted (3rd party)' },
];

// Manufacturing role + GMP posture are now per-row attributes on the consolidated
// list (formerly the separate Manufacturing Information section).
const MANUFACTURER_ROLES = [
  'api',
  'drug',
  'device',
  'assembly',
  'packaging',
  'sterilization',
] as const;
const GMP_STATUSES = ['compliant', 'pending', 'not_applicable'] as const;

// A row in the consolidated "Facilities & Sites Information" list — a site the
// company operates or contracts with. Base identity (name/type/address/DUNS/
// contact) + a relationship tag + optional legal form, manufacturing role, and
// self-attested GMP status + a Manufacturer-of-Record flag. Per-market
// registration (e.g. an FDA FEI) is TO — assembled at (facility × market) in the
// backend, not stored here.
export const facilitySchema = z.object({
  // Client-generated row identity (not user-facing, never sent as a real
  // regulatory field — `Company.base` is a schema-less dict server-side, so
  // an extra key here is harmless end-to-end). Lets the autosave hold-back
  // merge (mergeFacilitiesField, company-data.ts) key off ROW IDENTITY
  // instead of array position — useFieldArray.remove() shifts later rows'
  // indices down, so a positional lookup can substitute a DIFFERENT row's
  // last-known-good value once an earlier row has been removed.
  id: z.string().optional(),
  relationship: z.enum(FACILITY_RELATIONSHIPS, { message: 'Select the relationship' }),
  name: requiredText('Facility name is required', COMPANY_NAME_MAX),
  legal_form: z.string().optional(),
  // Carries the free-text value when legal_form is 'other' — same "Other"
  // pattern as Section 1's company_type (requireCompanyTypeOtherWhenSelected).
  // Optional at this layer; enforced conditionally on the composed schema,
  // which is where a row's sibling field is visible.
  legal_form_other: optionalShortText,
  type: z.enum(FACILITY_TYPES, { message: 'Select facility type' }),
  roles: z.array(z.enum(MANUFACTURER_ROLES)).optional(),
  duns: dunsSchema,
  address: requiredText('Address is required', ADDRESS_MAX),
  city: requiredText('City is required', CITY_MAX),
  state: optionalShortText,
  postal: requiredPostal,
  country: requiredText('Country is required', COUNTRY_MAX),
  phone: requiredText('Phone is required', PHONE_MAX)
    .refine(isValidPhone, PHONE_MSG),
  contact: requiredText('Contact person is required', PERSON_NAME_MAX),
  gmp_status: z.enum(GMP_STATUSES).optional(),
  is_manufacturer_of_record: z.boolean().optional(),
});

export type FacilityValues = z.infer<typeof facilitySchema>;

export const establishmentRegistrationSchema = z.object({
  // At least one facility is required to submit; at most one row can be
  // flagged Manufacturer of Record.
  facilities: z
    .array(facilitySchema)
    .min(1, 'At least one facility is required')
    .refine(
      (rows) => rows.filter((r) => r.is_manufacturer_of_record).length <= 1,
      { message: 'Only one facility can be the Manufacturer of Record' },
    ),
});

export type EstablishmentRegistrationValues = z.infer<typeof establishmentRegistrationSchema>;

export const blankFacility: FacilityValues = {
  relationship: 'own_site',
  name: '',
  legal_form: '',
  legal_form_other: '',
  type: 'manufacturing',
  roles: [],
  duns: '',
  address: '',
  city: '',
  state: '',
  postal: '',
  country: '',
  phone: '',
  contact: '',
  is_manufacturer_of_record: false,
};

export const establishmentRegistrationDefaults: Partial<EstablishmentRegistrationValues> = {
  // Starts empty — shows the "Add site" button + helper rather than a
  // pre-filled blank form. At least one facility is required before this
  // section (and submission) validates; see establishmentRegistrationSchema.
  facilities: [],
};

// ---------------------------------------------------------------------------
// Shared option lists for the consolidated Facilities & Sites list — the
// manufacturing-role multi-select and the (self-attested) GMP compliance status.
// FDA issues no cGMP certificate, so the status reads as self-attestation.
// ---------------------------------------------------------------------------

export const MANUFACTURER_ROLE_OPTIONS: { value: typeof MANUFACTURER_ROLES[number]; label: string }[] = [
  { value: 'api', label: 'API Manufacturing' },
  { value: 'drug', label: 'Drug Product Manufacturing' },
  { value: 'device', label: 'Device Manufacturing' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'sterilization', label: 'Sterilization' },
];

export const GMP_STATUS_OPTIONS: { value: typeof GMP_STATUSES[number]; label: string }[] = [
  { value: 'compliant', label: 'Self-attested compliant' },
  { value: 'pending', label: 'Working toward compliance' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

// ---------------------------------------------------------------------------
// Section 7 — Quality System Information (9 fields, 6 required)
// ---------------------------------------------------------------------------

const CAPA_VALUES = ['yes', 'no'] as const;
const GMP_CERT_VALUES = ['self_attested_compliant', 'working_toward_compliance', 'not_applicable'] as const;
const ISO_CERTS = ['iso13485', 'iso9001', 'iso14971', 'iso10993'] as const;

export const CAPA_OPTIONS: { value: typeof CAPA_VALUES[number]; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// FDA issues no cGMP certificate (compliance is via facility inspection), so
// this self-attestation is framed as a compliance status, not a credential —
// both the stored value AND the label read as honest compliance wording.
export const GMP_CERT_OPTIONS: { value: typeof GMP_CERT_VALUES[number]; label: string }[] = [
  { value: 'self_attested_compliant', label: 'Self-attested compliant' },
  { value: 'working_toward_compliance', label: 'Working toward compliance' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

// ISO 13485 / 9001 are QMS standards a company gets certified against by an
// accredited registrar, issuing a certificate with a number. ISO 14971 (risk
// management) and ISO 10993 (biocompatibility) are process/technical
// standards applied in a device's design and test file — there's no
// certificate or certificate number to record for them.
// `hasCertificateNumber` is a per-branch LITERAL (true/false), not a plain
// boolean — so `.filter((opt) => opt.hasCertificateNumber)` narrows `value`
// down to just 'iso13485' | 'iso9001', which is what makes the certificate-
// number field name template-literal type-check against CustomerAccountValues
// (only those two have a matching `*_certificate_number` field).
export const ISO_CERT_OPTIONS: (
  | { value: 'iso13485' | 'iso9001'; label: string; hasCertificateNumber: true }
  | { value: 'iso14971' | 'iso10993'; label: string; hasCertificateNumber: false }
)[] = [
  { value: 'iso13485', label: 'ISO 13485 (Medical Devices Quality Management)', hasCertificateNumber: true },
  { value: 'iso9001', label: 'ISO 9001 (Quality Management)', hasCertificateNumber: true },
  { value: 'iso14971', label: 'ISO 14971 (Risk Management)', hasCertificateNumber: false },
  { value: 'iso10993', label: 'ISO 10993 (Biocompatibility)', hasCertificateNumber: false },
];

export const qualitySystemSchema = z.object({
  qms_standard: requiredText('Quality management system standard is required', QMS_STANDARD_MAX),
  quality_head_name: requiredText('Quality head name is required', PERSON_NAME_MAX),
  quality_contact: requiredText('Quality contact email is required', EMAIL_MAX)
    .email('Enter a valid email address'),
  quality_procedures_id: optionalShortText,
  qms_scope: optionalLongText,
  // Company-level certifications (global quality posture). ISO certs + CE marking
  // are kept in the BASE per the user's call (company-level certs); CE marking
  // status also surfaces per-EU-market in the jurisdiction modules.
  gmp_certification: z.enum(GMP_CERT_VALUES, { message: 'Select cGMP compliance status' }),
  iso_certs: z.array(z.enum(ISO_CERTS)).optional(),
  // One certificate-number field per certifiable ISO_CERTS entry (see
  // hasCertificateNumber on ISO_CERT_OPTIONS) — the UI reveals the matching
  // input under a cert's checkbox only once it's checked. 14971/10993 have no
  // certificate to record, so they get no matching field here.
  iso13485_certificate_number: optionalShortText,
  iso9001_certificate_number: optionalShortText,
  ce_mark: optionalShortText,
  other_certifications: optionalShortText,
  capa_system: z.enum(CAPA_VALUES, { message: 'Select Yes or No' }),
  document_control: optionalShortText,
  change_control: optionalShortText,
});

export type QualitySystemValues = z.infer<typeof qualitySystemSchema>;

/** Section 5's schema with the cross-field certificate-number rule applied.
 *  `qualitySystemSchema` stays a plain ZodObject so `.shape` can still be
 *  spread into the composed form schema below (which re-applies the rule). */
export const qualitySystemSubmitSchema = qualitySystemSchema.superRefine(
  requireIsoCertificateNumbers,
);

export const qualitySystemDefaults: Partial<QualitySystemValues> = {
  quality_head_name: '',
  quality_contact: '',
  quality_procedures_id: '',
  qms_scope: '',
  iso_certs: [],
  iso13485_certificate_number: '',
  iso9001_certificate_number: '',
  ce_mark: '',
  other_certifications: '',
  document_control: '',
  change_control: '',
  qms_standard: '',
  // `capa_system` + `gmp_certification` intentionally omitted so their
  // dropdown placeholders show; `qms_standard` is now free text.
};

// ---------------------------------------------------------------------------
// Section 8 — Regulatory History
//
// Required radio (previous_submissions yes/no) + conditional submissions
// dynamic-list (shown only when yes) + always-shown inspections list.
// ---------------------------------------------------------------------------

const PREV_SUBMISSIONS_VALUES = ['yes', 'no'] as const;
// Mirrors the USA dossier agent's supported pathways (app/dossier/usa/config.py
// SUBMISSION_TYPE_CONFIG) across devices, drugs, and biologics — the vocabulary a
// company records a past FDA submission under. IDE is dropped: it's an
// investigational-device application, not one of the agent's marketing pathways.
const SUBMISSION_TYPES = [
  // Medical devices
  '510k', '510k-special', '510k-abbreviated', 'de_novo', 'pma',
  // Drugs
  'ind', 'nda', 'nda-505b2', 'anda',
  // Biologics
  'bla', 'biosimilar',
] as const;
const SUBMISSION_STATUSES = ['approved', 'pending', 'withdrawn'] as const;
const INSPECTION_OUTCOMES = ['nai', 'vai', 'oai'] as const;

export const SUBMISSION_TYPE_OPTIONS: { value: typeof SUBMISSION_TYPES[number]; label: string }[] = [
  // Medical devices
  { value: '510k', label: '510(k)' },
  { value: '510k-special', label: 'Special 510(k)' },
  { value: '510k-abbreviated', label: 'Abbreviated 510(k)' },
  { value: 'de_novo', label: 'De Novo' },
  { value: 'pma', label: 'PMA (Premarket Approval)' },
  // Drugs
  { value: 'ind', label: 'IND (Investigational New Drug)' },
  { value: 'nda', label: 'NDA (New Drug Application)' },
  { value: 'nda-505b2', label: 'NDA 505(b)(2)' },
  { value: 'anda', label: 'ANDA (Abbreviated New Drug Application)' },
  // Biologics
  { value: 'bla', label: 'BLA (Biologics License Application)' },
  { value: 'biosimilar', label: 'Biosimilar (351(k))' },
];

export const SUBMISSION_STATUS_OPTIONS: { value: typeof SUBMISSION_STATUSES[number]; label: string }[] = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

export const INSPECTION_OUTCOME_OPTIONS: { value: typeof INSPECTION_OUTCOMES[number]; label: string }[] = [
  { value: 'nai', label: 'NAI (No Action Indicated)' },
  { value: 'vai', label: 'VAI (Voluntary Action Indicated)' },
  { value: 'oai', label: 'OAI (Official Action Indicated)' },
];

export const fdaSubmissionSchema = z.object({
  type: z.enum(SUBMISSION_TYPES).or(z.literal('')).optional(),
  number: optionalShortText,
  product: optionalShortText,
  status: z.enum(SUBMISSION_STATUSES).or(z.literal('')).optional(),
});
export type FdaSubmissionValues = z.infer<typeof fdaSubmissionSchema>;

export const fdaInspectionSchema = z.object({
  date: z.string().optional(),
  outcome: z.enum(INSPECTION_OUTCOMES).or(z.literal('')).optional(),
});
export type FdaInspectionValues = z.infer<typeof fdaInspectionSchema>;

// US-market (TO) — FDA regulatory history. Renders in the US module; optional here
// so a company that doesn't sell into the US isn't blocked — `previous_submissions`
// is required once the US market is added (requirePreviousFdaSubmissions).
export const regulatoryHistorySchema = z.object({
  previous_submissions: z.enum(PREV_SUBMISSIONS_VALUES).or(z.literal('')).optional(),
  submissions: z.array(fdaSubmissionSchema).optional(),
  inspections: z.array(fdaInspectionSchema).optional(),
});
export type RegulatoryHistoryValues = z.infer<typeof regulatoryHistorySchema>;

export const blankFdaSubmission: FdaSubmissionValues = {
  type: '',
  number: '',
  product: '',
  status: '',
};
export const blankFdaInspection: FdaInspectionValues = {
  date: '',
  outcome: '',
};

export const regulatoryHistoryDefaults: Partial<RegulatoryHistoryValues> = {
  submissions: [],
  inspections: [{ ...blankFdaInspection }],
  // `previous_submissions` intentionally omitted so radio shows unselected.
};

// ---------------------------------------------------------------------------
// US MARKET (TO) — FDA establishment identifiers + U.S. Agent + FDA reg status.
//
// These render in the US jurisdiction module (stage 2), NOT the base. ALL
// optional at the schema level — a company that doesn't sell into the US isn't
// blocked from submitting; the "required for a US filing" rule is conditional
// (enforced when the US market is added). Maps to company_jurisdictions[US].data
// in the backend. FDA regulatory history + ESG (above) are also US-market.
// ---------------------------------------------------------------------------

const FDA_REG_STATUS_VALUES = ['registered', 'pending', 'not_required'] as const;

export const FDA_REG_STATUS_LIST_OPTIONS: { value: typeof FDA_REG_STATUS_VALUES[number]; label: string }[] = [
  { value: 'registered', label: 'Registered and Current' },
  { value: 'pending', label: 'Registration Pending' },
  { value: 'not_required', label: 'Not Required' },
];

/** A DEA registration number is 2 letters + 7 digits, the last of which is a
 *  check digit — the units digit of (d1+d3+d5) + 2·(d2+d4+d6). Assumes the
 *  2-letter/7-digit shape is already enforced (the regex owns the format error). */
function deaCheckDigitValid(v: string): boolean {
  if (!/^[A-Za-z]{2}\d{7}$/.test(v)) return true; // format error is owned by the regex
  const ds = v.slice(2); // the 7 digits
  const digit = (i: number): number => ds.charCodeAt(i) - 48;
  const check = (digit(0) + digit(2) + digit(4) + 2 * (digit(1) + digit(3) + digit(5))) % 10;
  return check === digit(6);
}

export const usMarketSchema = z.object({
  // FDA establishment identifiers
  tax_id: z
    .string()
    .regex(/^\d{2}-?\d{7}$/, 'Format: XX-XXXXXXX (9 digits, optional hyphen)')
    .or(z.literal(''))
    .optional(),
  // FEI = 7-10 digits (FDA issues as few as 7 for some device establishments).
  // Optional: it's assigned AFTER registration (up to ~90 days), so a newly
  // registered establishment may not have one yet (pending) — see
  // fda_registration_status + owner_operator_number as the interim identifiers.
  fei_number: z
    .string()
    .regex(/^\d{7,10}$/, 'FEI must be 7–10 digits')
    .or(z.literal(''))
    .optional(),
  establishment_registration_number: optionalShortText,
  owner_operator_number: optionalShortText,
  cage_code: optionalShortText,
  fda_registration_status: z.enum(FDA_REG_STATUS_VALUES).or(z.literal('')).optional(),
  // Drug/biologic-applicant identifiers (optional — blank for device applicants).
  // A Drug Master File cross-reference (Module 3.2.S) and a DEA registration for
  // controlled substances. DMF stays loose (normalized on blur); the DEA number is
  // format + check-digit validated when present (a formatter alone let 123456789 pass).
  dmf_number: optionalShortText,
  dea_registration_number: z
    .string()
    .regex(/^[A-Za-z]{2}\d{7}$/, 'DEA format: 2 letters + 7 digits (e.g., AB1234563)')
    .refine(deaCheckDigitValid, 'DEA check digit is invalid — re-check the number')
    .or(z.literal(''))
    .optional(),
  // Authorized U.S. Agent (FDA requires one for foreign-incorporated manufacturers)
  us_agent_name: optionalShortText,
  us_agent_company: optionalShortText,
  us_agent_address: optionalShortText,
  us_agent_city: optionalShortText,
  us_agent_state: optionalShortText,
  us_agent_postal: optionalPostal,
  us_agent_country: optionalShortText,
  us_agent_phone: optionalPhone,
  us_agent_email: optionalEmail,
});

export type UsMarketValues = z.infer<typeof usMarketSchema>;

export const usMarketDefaults: Partial<UsMarketValues> = {
  tax_id: '',
  fei_number: '',
  establishment_registration_number: '',
  owner_operator_number: '',
  cage_code: '',
  dmf_number: '',
  dea_registration_number: '',
  us_agent_name: '',
  us_agent_company: '',
  us_agent_address: '',
  us_agent_city: '',
  us_agent_state: '',
  us_agent_postal: '',
  us_agent_country: '',
  us_agent_phone: '',
  us_agent_email: '',
};

// ---------------------------------------------------------------------------
// CANADA MARKET (TO) — Health Canada (SOR/98-282) identifiers + Canadian Rep.
//
// Same shape/treatment as the US market: flat fields that live in the MAIN
// form, ALL optional (a company not selling into Canada isn't blocked), carved
// into company_jurisdictions['CA'].data on save (the FLAT_FORM_MARKETS registry
// in markets.ts owns the key-set). Renders in the CA jurisdiction module. The
// Canadian Representative is FDA's "U.S. Agent" analog — required for foreign
// manufacturers, surfaced via `ca_establishment_status`.
// ---------------------------------------------------------------------------

const CA_ESTABLISHMENT_STATUS_VALUES = ['home', 'third'] as const;

export const caMarketSchema = z.object({
  // Company & tax registration
  corporation_number: optionalShortText,
  business_number: optionalShortText,
  // Health Canada establishment
  hc_company_id: optionalShortText,
  mdel_number: optionalShortText,
  // MDL (Medical Device Licence) — the per-device PRODUCT licence for Class
  // II–IV devices, distinct from the establishment-level MDEL above. Company-
  // level + optional here so an applicant can record an already-issued licence
  // (or reference one for an amendment); a company may hold several.
  mdl_number: optionalShortText,
  del_number: optionalShortText,
  // Establishment declaration (drives the Canadian Rep block)
  ca_establishment_status: z.enum(CA_ESTABLISHMENT_STATUS_VALUES).or(z.literal('')).optional(),
  // Canadian Representative (required for foreign manufacturers)
  ca_rep_name: optionalShortText,
  ca_rep_company: optionalShortText,
  ca_rep_address: optionalShortText,
  ca_rep_city: optionalShortText,
  ca_rep_state: optionalShortText,
  ca_rep_postal: optionalPostal,
  ca_rep_country: optionalShortText,
  ca_rep_phone: optionalPhone,
  ca_rep_email: optionalEmail,
});

export type CaMarketValues = z.infer<typeof caMarketSchema>;

export const caMarketDefaults: Partial<CaMarketValues> = {
  corporation_number: '',
  business_number: '',
  hc_company_id: '',
  mdel_number: '',
  mdl_number: '',
  del_number: '',
  ca_establishment_status: '',
  ca_rep_name: '',
  ca_rep_company: '',
  ca_rep_address: '',
  ca_rep_city: '',
  ca_rep_state: '',
  ca_rep_postal: '',
  ca_rep_country: '',
  ca_rep_phone: '',
  ca_rep_email: '',
};

// ---------------------------------------------------------------------------
// AUSTRALIA MARKET (TO) — TGA (Therapeutic Goods Act 1989) Australian Sponsor.
//
// The Australian Sponsor is the local entity responsible for ARTG inclusion.
// It is always required for foreign manufacturers (no "not applicable" path,
// unlike the US Agent which is optional for domestic manufacturers).
// Keys are defined ONCE here and match the dossier reader (au_sponsor_*) and
// the persisted company_jurisdictions['AU'].data blob exactly — no divergence.
// ---------------------------------------------------------------------------

const AU_ESTABLISHMENT_STATUS_VALUES = [
  'sponsor_is_manufacturer',
  'third_party_sponsor',
] as const;

export const auMarketSchema = z.object({
  // Australian Sponsor identity
  au_sponsor_legal_name: optionalShortText,
  au_sponsor_contact_name: optionalShortText,
  // Australian Business Number (ABN) — 11 digits (spaces allowed, stripped on save)
  au_abn: z
    .string()
    .regex(/^\d{11}$/, 'ABN must be 11 digits')
    .or(z.literal(''))
    .optional(),
  // Sponsor relationship to manufacturer (drives the Sponsor block)
  au_establishment_status: z
    .enum(AU_ESTABLISHMENT_STATUS_VALUES)
    .or(z.literal(''))
    .optional(),
  // Australian Sponsor address
  au_sponsor_address: optionalShortText,
  au_sponsor_city: optionalShortText,
  au_sponsor_state: optionalShortText,
  au_sponsor_postal: optionalPostal,
  au_sponsor_country: optionalShortText,
  au_sponsor_phone: optionalPhone,
  au_sponsor_email: optionalEmail,
});

export type AuMarketValues = z.infer<typeof auMarketSchema>;

export const auMarketDefaults: Partial<AuMarketValues> = {
  au_sponsor_legal_name: '',
  au_sponsor_contact_name: '',
  au_abn: '',
  au_establishment_status: '',
  au_sponsor_address: '',
  au_sponsor_city: '',
  au_sponsor_state: '',
  au_sponsor_postal: '',
  au_sponsor_country: '',
  au_sponsor_phone: '',
  au_sponsor_email: '',
};

// ---------------------------------------------------------------------------
// Product Responsibility Declaration
//
// Conceptually per-product, but kept on the company account for now (a working
// company-level assumption) until the RA reviewer decides — so OPTIONAL, not
// blocking.
// ---------------------------------------------------------------------------

export const productResponsibilitySchema = z.object({
  product_owner: optionalShortText,
  ip_owner: optionalShortText,
  submission_sponsor: optionalShortText,
});

export type ProductResponsibilityValues = z.infer<typeof productResponsibilitySchema>;

export const productResponsibilityDefaults: Partial<ProductResponsibilityValues> = {
  product_owner: '',
  ip_owner: '',
  submission_sponsor: '',
};

// ---------------------------------------------------------------------------
// Financial Disclosure (FDA 21 CFR 54)
//
// Conceptually per-submission/clinical, but kept on the company account for now
// (working assumption) until the RA reviewer decides. All optional.
// ---------------------------------------------------------------------------

const FINANCIAL_DISCLOSURE_CERT_VALUES = ['certified', 'not_applicable'] as const;

export const financialDisclosureSchema = z.object({
  investigator_financial_interests: optionalLongText,
  sponsor_financial_arrangements: optionalLongText,
  financial_disclosure_cert: z
    .enum(FINANCIAL_DISCLOSURE_CERT_VALUES)
    .or(z.literal(''))
    .optional(),
});

export type FinancialDisclosureValues = z.infer<typeof financialDisclosureSchema>;

export const financialDisclosureDefaults: Partial<FinancialDisclosureValues> = {
  investigator_financial_interests: '',
  sponsor_financial_arrangements: '',
  // Explicitly '' (not omitted) so the radio group has a defined empty value.
  // Left undefined instead, a reset would write `undefined` into a registered
  // field and the previously-picked radio would stay visually selected.
  financial_disclosure_cert: '',
};

// ---------------------------------------------------------------------------
// Section 13 — Legal and Authorization Statements (13 fields, 12 required)
//
// Merged with the former "Authorized Company Official" section: the one liable
// company official IS the signatory (RA-confirmed, same person). The identity +
// contact block and the signature-authority radio moved here from Section 4.
// `signatory_name` / `signatory_title` keep their names — the dossier reads them.
// ---------------------------------------------------------------------------

const TRUTHFULNESS_CERT_VALUES = ['certified'] as const;
const SIGNATURE_AUTHORITY_VALUES = ['confirmed', 'pending'] as const;

export const legalAuthorizationSchema = z.object({
  // Identity + contact of the authorized company official / signatory.
  signatory_name: requiredText('Authorized signatory name is required', PERSON_NAME_MAX),
  signatory_title: requiredText('Title is required', JOB_TITLE_MAX),
  signatory_phone: requiredText('Phone number is required', PHONE_MAX)
    .refine(isValidPhone, PHONE_MSG),
  signatory_email: requiredText('Email is required', EMAIL_MAX)
    .email('Enter a valid email address'),
  signatory_address: requiredText('Address is required', ADDRESS_MAX),
  signatory_city: requiredText('City is required', CITY_MAX),
  signatory_state: optionalShortText,
  signatory_postal: requiredPostal,
  signatory_country: requiredText('Country is required', COUNTRY_MAX),
  signature_authority: z.enum(SIGNATURE_AUTHORITY_VALUES, {
    message: 'Confirm signature authority status',
  }),
  truthfulness_cert: z.enum(TRUTHFULNESS_CERT_VALUES, {
    message: 'You must certify truthfulness to submit',
  }),
  signatory_date: requiredText('Date is required'),
  digital_signature: requiredText('Electronic signature is required', PERSON_NAME_MAX),
});

/** The typed signature is the signatory's seal — it must BE their name.
 *  Compared case-insensitively with whitespace collapsed, so "john  SMITH"
 *  signs for "John Smith" but "whatever" cannot. Lives in a superRefine
 *  (applied here and on the composed form schema) because it's cross-field. */
function requireSignatureMatchesSignatory(
  v: { signatory_name?: string; digital_signature?: string },
  ctx: z.RefinementCtx,
): void {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const name = v.signatory_name ?? '';
  const sig = v.digital_signature ?? '';
  if (sig.trim() && name.trim() && norm(sig) !== norm(name)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['digital_signature'],
      message: 'Electronic signature must match the Authorized Signatory Name',
    });
  }
}

/** Signature authority must be affirmatively CONFIRMED to submit — 'pending'
 *  is a legal radio value (lets the user leave the section mid-flow without a
 *  schema error) but must not pass overall validation, mirroring the
 *  backend's `_validate_attestation` check (customer_lifecycle.py). Guards on
 *  the field being set at all so an unanswered radio still surfaces the base
 *  enum's own "required" error instead of double-erroring. */
function requireSignatureAuthorityConfirmed(
  v: { signature_authority?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.signature_authority && v.signature_authority !== 'confirmed') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['signature_authority'],
      message: 'Signature authority must be confirmed before you can submit',
    });
  }
}

export const legalAuthorizationSubmitSchema = legalAuthorizationSchema
  .superRefine(requireSignatureMatchesSignatory)
  .superRefine(requireSignatureAuthorityConfirmed);

/** The contact-block fields a foreign agent / representative must fill. Company
 *  and State/Province stay optional: FDA lets the agent be an individual (not a
 *  firm), and many locales don't use a state. Suffixes match both the `us_agent_`
 *  and `ca_rep_` field families and the shared <AddressFields> block. */
const FOREIGN_CONTACT_FIELDS = [
  'name',
  'address',
  'city',
  'postal',
  'country',
  'phone',
  'email',
] as const;

/** Flag every empty field of a required foreign contact block. `who` is the
 *  party name ("U.S. Agent"), `reason` the trigger clause. */
function requireContactBlock(
  ctx: z.RefinementCtx,
  prefix: 'ca_rep' | 'us_agent' | 'au_sponsor' | 'uk_rep' | 'ec_rep',
  values: Record<string, unknown>,
  who: string,
  reason: string,
  nameSuffix = 'name',
): void {
  const humanize: Record<string, string> = { postal: 'postal code', legal_name: 'legal name' };
  for (const field of FOREIGN_CONTACT_FIELDS) {
    // The "name" field varies by party (US Agent / CA Rep use `_name`, the AU
    // Sponsor uses `_legal_name`); the rest share the <AddressFields> suffixes.
    const suffix = field === 'name' ? nameSuffix : field;
    const key = `${prefix}_${suffix}`;
    const value = values[key];
    if (typeof value !== 'string' || !value.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: `${who} ${humanize[suffix] ?? suffix} is required ${reason}`,
      });
    }
  }
}

/** Section 1's company_type enum has no "please specify" slot of its own —
 *  a free-text field carries it when the user picks 'other'. Conditional so
 *  picking any of the listed types never requires it. */
export function requireCompanyTypeOtherWhenSelected(
  v: { company_type?: string; company_type_other?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.company_type !== 'other') return;
  if (!(v.company_type_other ?? '').trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['company_type_other'],
      message: 'Enter the company type',
    });
  }
}

/** A facility row's legal_form enum has no "please specify" slot of its own,
 *  same gap as Section 1's company_type — a free-text field on the row carries
 *  it when 'other' is picked. Conditional per row: only a row with 'other'
 *  selected requires its sibling; every other row is untouched. */
export function requireFacilityLegalFormOtherWhenSelected(
  v: { facilities?: readonly { legal_form?: string; legal_form_other?: string }[] },
  ctx: z.RefinementCtx,
): void {
  (v.facilities ?? []).forEach((row, i) => {
    if (row.legal_form !== 'other') return;
    if (!(row.legal_form_other ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['facilities', i, 'legal_form_other'],
        message: 'Enter the legal form',
      });
    }
  });
}

/** ISO 13485 / ISO 9001 are certifications issued by an accredited registrar,
 *  so checking one asserts a certificate exists — and a certificate without its
 *  number is unverifiable by a reviewer. The UI reveals the matching number
 *  input the moment the box is ticked; this makes filling it mandatory rather
 *  than advisory. Conditional: unchecked certs (and 14971 / 10993, which have
 *  no certificate at all) never require anything. Lives in a superRefine
 *  because it's cross-field (`iso_certs` gates the `*_certificate_number`). */
export function requireIsoCertificateNumbers(
  v: {
    iso_certs?: readonly string[];
    iso13485_certificate_number?: string;
    iso9001_certificate_number?: string;
  },
  ctx: z.RefinementCtx,
): void {
  const checked = v.iso_certs ?? [];
  for (const opt of ISO_CERT_OPTIONS) {
    if (!opt.hasCertificateNumber) continue;
    if (!checked.includes(opt.value)) continue;
    const path = `${opt.value}_certificate_number` as const;
    if (!(v[path] ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        // Names the standard: the two inputs sit one above the other, and a
        // bare "required" under the second is ambiguous about which is missing.
        message: `${opt.label.split(' (')[0]} certificate number is required`,
      });
    }
  }
}

/** Health Canada (SOR/98-282) requires a resident Canadian representative when
 *  the manufacturer is established OUTSIDE Canada. Conditional so a domestic (or
 *  undeclared) entity isn't blocked. Mirrors the U.S. Agent rule below. */
function requireCanadianRepWhenForeign(
  v: { ca_establishment_status?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.ca_establishment_status !== 'third') return;
  requireContactBlock(
    ctx,
    'ca_rep',
    v as Record<string, unknown>,
    'Canadian Representative',
    'for foreign manufacturers',
  );
}

type EstablishmentFields = {
  hq_country?: string;
  facilities?: readonly { relationship?: string; country?: string; roles?: readonly string[] }[];
};

/** True only when the country is KNOWN and outside the set. A blank or unrecognised value
 *  (old free-typed data) never triggers a requirement — it can't be told apart, so the
 *  rule doesn't guess; the dossier placeholders the field instead. */
const isOutside = (country: string | undefined, iso2: ReadonlySet<string>) => {
  const code = countryToIso2OrNull(country);
  return code !== null && !iso2.has(code);
};

/** True when the company operates an establishment outside the United States: its
 *  registered headquarters, or any of its OWN sites. 21 CFR 807.3(c) defines an
 *  establishment as "a place of business under one management at one general
 *  physical location", so this is about where the company operates, not where it is
 *  incorporated. Contracted and affiliated sites are separate firms that register
 *  (and name their own U.S. agent) themselves. A blank country never counts. */
export function hasEstablishmentOutsideUs(v: EstablishmentFields): boolean {
  const us = new Set(['US']);
  return (
    isOutside(v.hq_country, us) ||
    (v.facilities ?? []).some((f) => f.relationship === 'own_site' && isOutside(f.country, us))
  );
}

/** FDA: each foreign establishment whose device or drug is imported or offered for
 *  import into the United States must name a United States agent (21 CFR 807.40(b),
 *  207.69(b)). Applies once the company adds the US market (MARKET_RULE_CHECKS). */
export function requireUsAgentWhenForeign(v: EstablishmentFields, ctx: z.RefinementCtx): void {
  if (!hasEstablishmentOutsideUs(v)) return;
  requireContactBlock(
    ctx,
    'us_agent',
    v as Record<string, unknown>,
    'U.S. Agent',
    'for establishments outside the United States',
  );
}

/** "Previous FDA Submissions" (Yes/No, marked required) must be answered once the
 *  company adds the US market (MARKET_RULE_CHECKS). */
export function requirePreviousFdaSubmissions(
  v: { previous_submissions?: unknown },
  ctx: z.RefinementCtx,
): void {
  if (v.previous_submissions === 'yes' || v.previous_submissions === 'no') return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['previous_submissions'],
    message: 'Previous FDA Submissions is required',
  });
}

/** EU Member States + the EEA states where the MDR/IVDR also apply (Iceland,
 *  Liechtenstein, Norway). */
const EU_EEA_ISO2: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
]);

/** EU MDR/IVDR Art. 11(1): "Where the manufacturer of a device is not established in a
 *  Member State, the device may only be placed on the Union market if the manufacturer
 *  designates a sole authorised representative." Devices only (any facility with a
 *  'device' manufacturing role) — medicines have no authorised representative; there the
 *  marketing-authorisation holder itself must be in the EEA. Establishment = the
 *  registered (HQ) address. Applies once the company adds the EU market. */
export function needsEuAuthorisedRep(v: EstablishmentFields): boolean {
  return (
    isOutside(v.hq_country, EU_EEA_ISO2) &&
    (v.facilities ?? []).some((f) => f.roles?.includes('device'))
  );
}

/** The EU Authorised Representative block, checked by the EU module when
 *  `needsEuAuthorisedRep` holds. */
export const euAuthorisedRepCheckSchema = z
  .looseObject({})
  .superRefine((v, ctx) =>
    requireContactBlock(
      ctx,
      'ec_rep',
      v as Record<string, unknown>,
      'EU Authorised Representative',
      'for device manufacturers outside the EU/EEA',
    ),
  );

/** The TGA requires a local Australian Sponsor for ARTG inclusion when the
 *  manufacturer is NOT itself the sponsor (a foreign manufacturer). Conditional
 *  so a manufacturer-is-sponsor (or undeclared) entity isn't blocked. Mirrors
 *  US/CA; the sponsor's name field is `au_sponsor_legal_name`. */
function requireAuSponsorWhenForeign(
  v: { au_establishment_status?: string },
  ctx: z.RefinementCtx,
): void {
  if (v.au_establishment_status !== 'third_party_sponsor') return;
  requireContactBlock(
    ctx,
    'au_sponsor',
    v as Record<string, unknown>,
    'Australian Sponsor',
    'for foreign manufacturers seeking ARTG inclusion',
    'legal_name',
  );
}

/** The MHRA requires a UK Responsible Person (UKRP) for any manufacturer NOT
 *  established in Great Britain (EU/EEA or third-country, post-Brexit). Mirrors
 *  US/CA/AU. Attached to the UK module's own `ukMarketSchema` (the trigger and
 *  contact fields self-persist there, outside the composed customer schema). */
export function requireUkrpWhenForeign(
  v: { uk_establishment_status?: string; [k: string]: unknown },
  ctx: z.RefinementCtx,
): void {
  if (v.uk_establishment_status !== 'eu_eea' && v.uk_establishment_status !== 'third') return;
  requireContactBlock(ctx, 'uk_rep', v, 'UK Responsible Person', 'for non-UK manufacturers');
}

export const caMarketSubmitSchema =
  caMarketSchema.superRefine(requireCanadianRepWhenForeign);

export const auMarketSubmitSchema =
  auMarketSchema.superRefine(requireAuSponsorWhenForeign);

export type LegalAuthorizationValues = z.infer<typeof legalAuthorizationSchema>;

export const legalAuthorizationDefaults: Partial<LegalAuthorizationValues> = {
  signatory_name: '',
  signatory_title: '',
  signatory_phone: '',
  signatory_email: '',
  signatory_address: '',
  signatory_city: '',
  signatory_state: '',
  signatory_postal: '',
  signatory_country: '',
  signatory_date: '',
  digital_signature: '',
  // `signature_authority` intentionally omitted so the placeholder shows and
  // Zod kicks in on submit.
};

// ---------------------------------------------------------------------------
// Section 14 — Corporate Profile Narrative (5 fields, all optional)
// ---------------------------------------------------------------------------

export const corporateProfileSchema = z.object({
  company_overview: optionalLongText,
  mission_statement: optionalLongText,
  core_technology: optionalLongText,
  key_products: optionalLongText,
  years_experience: z
    .number()
    .int()
    .min(0)
    .max(200)
    .optional(),
});

export type CorporateProfileValues = z.infer<typeof corporateProfileSchema>;

export const corporateProfileDefaults: Partial<CorporateProfileValues> = {
  company_overview: '',
  mission_statement: '',
  core_technology: '',
  key_products: '',
};


// ---------------------------------------------------------------------------
// Full customer-account schema — composed from per-section schemas.
//
// All 15 sections implemented.
// ---------------------------------------------------------------------------

export const customerAccountSchema = companyIdentificationSchema
  .extend(corporateHeadquartersSchema.shape)
  .extend(fdaContactSchema.shape)
  .extend(establishmentRegistrationSchema.shape)
  .extend(qualitySystemSchema.shape)
  .extend(regulatoryHistorySchema.shape)
  .extend(usMarketSchema.shape)
  .extend(caMarketSchema.shape)
  .extend(auMarketSchema.shape)
  .extend(productResponsibilitySchema.shape)
  .extend(financialDisclosureSchema.shape)
  .extend(legalAuthorizationSchema.shape)
  .extend(corporateProfileSchema.shape)
  .superRefine(requireSignatureMatchesSignatory)
  .superRefine(requireSignatureAuthorityConfirmed)
  .superRefine(requireCompanyTypeOtherWhenSelected)
  .superRefine(requireFacilityLegalFormOtherWhenSelected)
  .superRefine(requireIsoCertificateNumbers);
// The U.S. Agent / Canadian Representative / Australian Sponsor rules are NOT
// here: each belongs to its market and runs only once that market is added (see
// MARKET_RULE_CHECKS + customerAccountResolver below).

export type CustomerAccountValues = z.infer<typeof customerAccountSchema>;

// ---------------------------------------------------------------------------
// customerAccountResolver — wraps zodResolver(customerAccountSchema) to work
// around a zod v4 behavior: a `.superRefine()` attached to an object is
// skipped ENTIRELY the moment ANY field on that same object has a fatal
// type-level issue (a required enum/number left blank), even one completely
// unrelated to the refinement. In this 15-section wizard SOME field is
// essentially always blank while the user is mid-flow, so every superRefine
// above (`requireCompanyTypeOtherWhenSelected` included) silently never fired
// until the whole form was otherwise complete — the required-when-"Other"
// errors never appeared. These standalone, minimally-shaped schemas below
// touch only the 2 fields each check needs, so an unrelated blank field
// elsewhere can't abort them; the resolver runs them independently and merges
// any issues on top of the normal per-field validation.
// ---------------------------------------------------------------------------

const companyTypeOtherCheckSchema = z
  .object({
    company_type: z.string().optional(),
    company_type_other: z.string().optional(),
  })
  .superRefine(requireCompanyTypeOtherWhenSelected);

const facilityLegalFormOtherCheckSchema = z
  .object({
    facilities: z
      .array(
        z.object({
          legal_form: z.string().optional(),
          legal_form_other: z.string().optional(),
        }),
      )
      .optional(),
  })
  .superRefine(requireFacilityLegalFormOtherWhenSelected);

const isoCertificateNumbersCheckSchema = z
  .object({
    iso_certs: z.array(z.string()).optional(),
    iso13485_certificate_number: z.string().optional(),
    iso9001_certificate_number: z.string().optional(),
  })
  .superRefine(requireIsoCertificateNumbers);

// requireSignatureAuthorityConfirmed is deliberately NOT here: 'pending' is
// meant to let the user leave Section 8 mid-flow, and only blocks at submit.
const signatureMatchesSignatoryCheckSchema = z
  .object({
    signatory_name: z.string().optional(),
    digital_signature: z.string().optional(),
  })
  .superRefine(requireSignatureMatchesSignatory);

const ABORT_IMMUNE_CROSS_FIELD_CHECKS = [
  companyTypeOtherCheckSchema,
  facilityLegalFormOtherCheckSchema,
  isoCertificateNumbersCheckSchema,
  signatureMatchesSignatoryCheckSchema,
];

// ---------------------------------------------------------------------------
// Target markets are independent. A flat-form market's fields and its
// "foreign company" rule only count once the company has ADDED that market, so
// an EU- or Australia-only company never needs the US (or any other market)
// filled in to submit. The wizard passes the added market codes as the resolver
// context. Each rule check is standalone (like the checks above), so it fires
// even while unrelated sections are still blank.
// ---------------------------------------------------------------------------

/** Form keys owned by each flat-form market — the one definition; the market
 *  registry (`features/customer/markets.ts`) builds from it. */
export const FLAT_FORM_MARKET_FIELDS: Readonly<Record<'US' | 'CA' | 'AU', readonly string[]>> = {
  // FDA establishment identifiers + U.S. Agent + FDA regulatory history.
  US: [...Object.keys(usMarketSchema.shape), ...Object.keys(regulatoryHistorySchema.shape)],
  // Health Canada identifiers (Company ID, MDEL, …) + Canadian Representative.
  CA: Object.keys(caMarketSchema.shape),
  // TGA Australian Sponsor block.
  AU: Object.keys(auMarketSchema.shape),
};

// Loose objects: the rules read the whole contact block off the values.
const MARKET_RULE_CHECKS: Readonly<Record<keyof typeof FLAT_FORM_MARKET_FIELDS, z.ZodType>> = {
  US: z.looseObject({}).superRefine((v, ctx) => {
    requireUsAgentWhenForeign(v, ctx);
    requirePreviousFdaSubmissions(v, ctx);
  }),
  CA: z.looseObject({ ca_establishment_status: z.string().optional() }).superRefine(requireCanadianRepWhenForeign),
  AU: z.looseObject({ au_establishment_status: z.string().optional() }).superRefine(requireAuSponsorWhenForeign),
};

/** The choices that switch a market rule on (a foreign-manufacturer status makes
 *  the local representative/sponsor required). The wizard re-validates when one
 *  changes so the newly required fields turn red right away. */
export const MARKET_RULE_TRIGGER_FIELDS = [
  'ca_establishment_status',
  'au_establishment_status',
] as const;

/** Resolver context: the market codes the company has added (absent = none). */
export type CustomerAccountContext = { addedMarkets: readonly string[] };

/** The values as validation should see them: every market that ISN'T added is
 *  reset to its blank defaults, so whatever it still holds (a removed market's
 *  leftovers included) can't block the form. Validation-only — saving and
 *  submit read the form's own values. */
function withUnaddedMarketsBlank(
  values: CustomerAccountValues,
  added: ReadonlySet<string>,
): CustomerAccountValues {
  const out: Record<string, unknown> = { ...values };
  const defaults = customerAccountDefaults as Record<string, unknown>;
  for (const [code, keys] of Object.entries(FLAT_FORM_MARKET_FIELDS)) {
    if (added.has(code)) continue;
    for (const key of keys) out[key] = defaults[key];
  }
  return out as CustomerAccountValues;
}

/** Deep-clones only plain objects/arrays — resolver error trees are plain
 *  data (`{type, message}` leaves), never DOM refs or class instances, so a
 *  structural recursive clone is enough and avoids `structuredClone` risk. */
function cloneErrorTree<T>(value: T): T {
  if (Array.isArray(value)) return value.map(cloneErrorTree) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = cloneErrorTree(v);
    return out as T;
  }
  return value;
}

/** Sets a single error at a zod issue path (e.g. `['facilities', 0,
 *  'legal_form_other']`) onto a cloned error tree, creating containers along
 *  the way. Setting one leaf at a time — rather than merging two whole
 *  trees with `{...a, ...b}` — is what lets this survive a SIBLING error
 *  already present under the same top-level array key (e.g. `facilities`
 *  also has a missing-`name` error on some row): a shallow merge would have
 *  one source's `facilities` entry wholesale replace the other's. */
function setErrorAtPath(
  tree: Record<string, unknown>,
  path: PropertyKey[],
  error: { type: string; message: string },
): void {
  // zod issue paths from our own `ctx.addIssue({ path: [...] })` calls are
  // always string/number segments (never a symbol) — `PropertyKey` is just
  // the type zod's `ZodIssue.path` declares.
  let node = tree;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i] as string | number;
    const existing = node[key];
    if (existing == null || typeof existing !== 'object') {
      node[key] = typeof path[i + 1] === 'number' ? [] : {};
    }
    node = node[key] as Record<string, unknown>;
  }
  node[path[path.length - 1] as string | number] = error;
}

export const customerAccountResolver: Resolver<
  CustomerAccountValues,
  CustomerAccountContext | undefined
> = async (values, context, options) => {
  const added = new Set(context?.addedMarkets ?? []);
  const checked = withUnaddedMarketsBlank(values, added);
  const base = await zodResolver(customerAccountSchema)(checked, context, options);

  const marketChecks = Object.entries(MARKET_RULE_CHECKS)
    .filter(([code]) => added.has(code))
    .map(([, check]) => check);
  const extraIssues: { path: PropertyKey[]; type: string; message: string }[] = [];
  for (const checkSchema of [...ABORT_IMMUNE_CROSS_FIELD_CHECKS, ...marketChecks]) {
    const result = checkSchema.safeParse(checked);
    if (result.success) continue;
    for (const issue of result.error.issues) {
      extraIssues.push({ path: issue.path, type: issue.code, message: issue.message });
    }
  }

  if (extraIssues.length === 0) return base;

  const errors = cloneErrorTree((base.errors ?? {}) as Record<string, unknown>);
  for (const { path, type, message } of extraIssues) {
    setErrorAtPath(errors, path, { type, message });
  }

  // `ResolverResult` is a discriminated union (ResolverSuccess has
  // `values: TFieldValues`; ResolverError narrows `values` to `{}`). This
  // object always carries the full `base.values` alongside non-empty
  // `errors`, which no single member of that union describes exactly — cast
  // rather than fight the discriminant; the runtime shape (values + errors)
  // is exactly what react-hook-form's resolver contract expects.
  return { values: base.values, errors } as ResolverResult<CustomerAccountValues>;
};

export const customerAccountDefaults: Partial<CustomerAccountValues> = {
  ...companyIdentificationDefaults,
  ...corporateHeadquartersDefaults,
  ...fdaContactDefaults,
  ...establishmentRegistrationDefaults,
  ...qualitySystemDefaults,
  ...regulatoryHistoryDefaults,
  ...usMarketDefaults,
  ...caMarketDefaults,
  ...auMarketDefaults,
  ...productResponsibilityDefaults,
  ...financialDisclosureDefaults,
  ...legalAuthorizationDefaults,
  ...corporateProfileDefaults,
};
